import { P2GLink, P2GText } from '@parcel2go/core-ui';
import { LinkModel } from './LinkModel';
import { useAuth } from 'react-oidc-context';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';
import BrSplitter from '~/apps/smartsend/lib/helpers/BrSplitter';
import { useLinkTitle } from './useLinkTitle';
import { useEffect } from 'react';
import { generateSwrCacheKeyForLink } from '../labels/label-lib';

export interface props {
  link: LinkModel;
  tag: string;
  onDownloaded?: () => void;
}

type LabelData = {
  result: {
    preSignedUrl: string;
  };
  success: boolean;
  error: object;
};

const PreSignedLink = ({ link, onDownloaded }: props) => {
  const { title } = useLinkTitle();
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();

  const { data, isLoading } = useSWR<LabelData>(
    generateSwrCacheKeyForLink(link),
    () =>
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, link.Href, {
        method: 'POST',
        body: JSON.stringify(link.Meta || {}),
        headers: { 'Content-Type': 'application/json' }
      })
        .then((res) => res.json())
        .catch(capturePromiseException),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshInterval: (d) => (d === undefined ? 1000 : d?.result?.preSignedUrl ? 0 : 5000)
      // Refresh every second if we dont have a pre-signed URL
    }
  );

  useEffect(() => {
    if (data?.result?.preSignedUrl && onDownloaded) {
      onDownloaded();
    }
  }, [onDownloaded, data?.result?.preSignedUrl]);

  if (isLoading || !data?.result?.preSignedUrl) {
    return <P2GText varian="body2">Loading...</P2GText>;
  }

  return (
    <P2GLink target="_blank" href={data.result.preSignedUrl}>
      <BrSplitter label={title(link)} />
    </P2GLink>
  );
};

export default PreSignedLink;
